import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Paper,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Slide,
  Zoom,
  Dialog,
  DialogContent,
  IconButton,
  Badge
} from '@mui/material';
import {
  ArrowRight,
  Filter,
  X,
  ExternalLink,
  Calendar,
  MapPin,
  Users,
  Home,
  Building2,
  Hotel,
  GraduationCap,
  Eye,
  Heart,
  Star,
  Award,
  Palette,
  Lightbulb,
  Target,
  Sparkles,
  CheckCircle,
  Clock
} from 'lucide-react';

// Project Card Component
const ProjectCard = ({ project, delay, onClick }) => {
  const [hovered, setHovered] = useState(false);

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'residential': return Home;
      case 'commercial': return Building2;
      case 'hospitality': return Hotel;
      case 'institutional': return GraduationCap;
      default: return Home;
    }
  };

  const CategoryIcon = getCategoryIcon(project.category);

  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          },
          '&:hover .project-overlay': {
            opacity: 1
          },
          '&:hover .project-image': {
            transform: 'scale(1.05)'
          }
        }}
        onClick={() => onClick(project)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            height: 280,
            overflow: 'hidden',
            background: project.gradient || 'linear-gradient(135deg, #FF5722, #FF8A65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Project Image Placeholder with Icons */}
          <Box
            className="project-image"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              transition: 'transform 0.6s ease',
              p: 3,
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyItems: 'center'
            }}
          >
            {project.icons.slice(0, 6).map((IconComponent, index) => (
              <Box
                key={index}
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  animation: `iconBounce 2s ease-in-out ${index * 0.3}s infinite alternate`,
                  transform: hovered ? `scale(${1.1 + (index % 2) * 0.1})` : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  '@keyframes iconBounce': {
                    '0%': { transform: 'translateY(0)' },
                    '100%': { transform: 'translateY(-6px)' }
                  }
                }}
              >
                <IconComponent size={24 + index} />
              </Box>
            ))}
          </Box>
          
          <Box
            className="project-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              p: 3,
              opacity: 0,
              transition: 'opacity 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CategoryIcon size={16} style={{ marginRight: 8, color: '#FF5722' }} />
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#FF5722',
                  fontWeight: 600,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                {project.category}
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              color="white" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              {project.title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: 'rgba(255,255,255,0.8)',
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              {project.description}
            </Typography>
            <Button 
              variant="outlined"
              size="small"
              endIcon={<Eye size={16} />}
              sx={{ 
                alignSelf: 'flex-start',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                borderRadius: 0,
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: 1,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CategoryIcon size={18} style={{ marginRight: 8, color: '#FF5722' }} />
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#FF5722',
                fontWeight: 600,
                letterSpacing: 1,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              {project.category}
            </Typography>
          </Box>
          <Typography 
            variant="h6" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {project.title}
          </Typography>
          <Typography 
            variant="body2" 
            color="#666"
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {project.description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

// Project Detail Modal
const ProjectDetailModal = ({ project, open, onClose }) => {
  if (!project) return null;

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'residential': return Home;
      case 'commercial': return Building2;
      case 'hospitality': return Hotel;
      case 'institutional': return GraduationCap;
      default: return Home;
    }
  };

  const CategoryIcon = getCategoryIcon(project.category);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          borderRadius: 0,
          border: '1px solid #E0E0E0',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              background: 'rgba(255,255,255,0.9)',
              color: '#1A1A1A',
              border: '1px solid #E0E0E0',
              borderRadius: 0,
              '&:hover': {
                background: '#FFFFFF',
                borderColor: '#FF5722'
              }
            }}
          >
            <X size={20} />
          </IconButton>

          {/* Project Image */}
          <Box
            sx={{
              height: 300,
              background: project.gradient || 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 3,
                p: 4,
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyItems: 'center'
              }}
            >
              {[...project.icons, ...project.icons.slice(0, 2)].map((IconComponent, index) => (
                <Box
                  key={index}
                  sx={{
                    color: 'white',
                    opacity: 0.8,
                    animation: `modalIconFloat 3s ease-in-out ${index * 0.2}s infinite alternate`,
                    '@keyframes modalIconFloat': {
                      '0%': { transform: 'translateY(0) rotate(0deg)' },
                      '100%': { transform: 'translateY(-10px) rotate(5deg)' }
                    }
                  }}
                >
                  <IconComponent size={28 + (index % 3) * 6} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Project Details */}
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CategoryIcon size={20} style={{ marginRight: 12, color: '#FF5722' }} />
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#FF5722',
                  fontWeight: 600,
                  letterSpacing: 2,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                {project.category}
              </Typography>
            </Box>

            <Typography 
              variant="h4" 
              color="#1A1A1A" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              {project.title}
            </Typography>

            <Typography 
              variant="body1" 
              color="#666" 
              paragraph
              sx={{ 
                lineHeight: 1.6,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              {project.fullDescription || project.description}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Calendar size={18} style={{ marginRight: 12, color: '#FF5722' }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="#999"
                      sx={{ 
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Completion Date
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="#1A1A1A"
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {project.completionDate}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MapPin size={18} style={{ marginRight: 12, color: '#FF5722' }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="#999"
                      sx={{ 
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Location
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="#1A1A1A"
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {project.location}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Users size={18} style={{ marginRight: 12, color: '#FF5722' }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="#999"
                      sx={{ 
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Project Size
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="#1A1A1A"
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {project.size}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Clock size={18} style={{ marginRight: 12, color: '#FF5722' }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="#999"
                      sx={{ 
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Duration
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="#1A1A1A"
                      sx={{ 
                        fontWeight: 500,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {project.duration}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {project.features && (
              <Box sx={{ mt: 3 }}>
                <Typography 
                  variant="h6" 
                  color="#1A1A1A" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Key Features
                </Typography>
                <Grid container spacing={1}>
                  {project.features.map((feature, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={feature}
                        size="small"
                        sx={{
                          background: 'rgba(255,87,34,0.08)',
                          color: '#FF5722',
                          border: '1px solid rgba(255,87,34,0.2)',
                          borderRadius: 0,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Filter Chip Component
const FilterChip = ({ label, active, onClick, count }) => {
  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          {count > 0 && (
            <Badge
              badgeContent={count}
              sx={{
                ml: 1,
                '& .MuiBadge-badge': {
                  background: active ? 'white' : '#FF5722',
                  color: active ? '#FF5722' : 'white',
                  fontSize: '0.7rem',
                  height: 16,
                  minWidth: 16
                }
              }}
            />
          )}
        </Box>
      }
      onClick={onClick}
      sx={{
        background: active ? 'linear-gradient(135deg, #FF5722, #FF8A65)' : '#FFFFFF',
        color: active ? 'white' : '#1A1A1A',
        border: `1px solid ${active ? '#FF5722' : '#E0E0E0'}`,
        borderRadius: 0,
        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
        '&:hover': {
          background: active ? 'linear-gradient(135deg, #FF5722, #FF8A65)' : '#F8F8F8',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        },
        transition: 'all 0.3s ease',
        fontWeight: active ? 600 : 400
      }}
    />
  );
};

const PortfolioPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowElements(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Sample portfolio data with enhanced details
  const projects = [
    {
      id: 1,
      title: 'Luxury Penthouse',
      category: 'Residential',
      description: 'Modern luxury living with panoramic city views',
      fullDescription: 'A stunning penthouse transformation featuring contemporary design elements, smart home integration, and breathtaking city views. This project showcases our ability to blend luxury with functionality.',
      icons: [Home, Lightbulb, Heart, Sparkles, Eye, Star],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      location: 'Banjara Hills, Hyderabad',
      completionDate: 'March 2024',
      size: '4,500 sq ft',
      duration: '8 months',
      features: ['Smart Home Integration', 'Premium Materials', 'Custom Furniture', 'Ambient Lighting']
    },
    {
      id: 2,
      title: 'Modern Office Space',
      category: 'Commercial',
      description: 'Collaborative workspace designed for innovation',
      fullDescription: 'A cutting-edge office design that promotes collaboration and productivity. Features include open workspaces, private meeting rooms, and recreational areas that boost employee morale.',
      icons: [Building2, Users, Target, Lightbulb, CheckCircle, Award],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      location: 'HITEC City, Hyderabad',
      completionDate: 'January 2024',
      size: '12,000 sq ft',
      duration: '6 months',
      features: ['Open Concept', 'Acoustic Solutions', 'Ergonomic Design', 'Green Elements']
    },
    {
      id: 3,
      title: 'Boutique Hotel',
      category: 'Hospitality',
      description: 'Intimate hospitality experience with local charm',
      fullDescription: 'A boutique hotel design that captures the essence of local culture while providing modern amenities. Each space tells a story and creates memorable experiences for guests.',
      icons: [Hotel, Star, Heart, Palette, Sparkles, Award],
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      location: 'Jubilee Hills, Hyderabad',
      completionDate: 'December 2023',
      size: '8,000 sq ft',
      duration: '10 months',
      features: ['Cultural Elements', 'Luxury Amenities', 'Sustainable Design', 'Guest Experience']
    },
    {
      id: 4,
      title: 'Contemporary Villa',
      category: 'Residential',
      description: 'Minimalist design with natural elements',
      fullDescription: 'A contemporary villa that embraces minimalism while incorporating natural materials and abundant natural light. The design creates a serene living environment.',
      icons: [Home, Palette, Eye, Lightbulb, Heart, CheckCircle],
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      location: 'Gachibowli, Hyderabad',
      completionDate: 'February 2024',
      size: '6,200 sq ft',
      duration: '7 months',
      features: ['Natural Materials', 'Minimalist Design', 'Large Windows', 'Indoor-Outdoor Flow']
    },
    {
      id: 5,
      title: 'Corporate Headquarters',
      category: 'Commercial',
      description: 'Executive spaces that inspire leadership',
      fullDescription: 'A sophisticated corporate headquarters featuring executive offices, conference facilities, and collaborative spaces designed to reflect the company\'s values and culture.',
      icons: [Building2, Award, Target, Users, Star, CheckCircle],
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      location: 'Financial District, Hyderabad',
      completionDate: 'November 2023',
      size: '15,000 sq ft',
      duration: '9 months',
      features: ['Executive Suites', 'Conference Facilities', 'Brand Integration', 'Technology Integration']
    },
    {
      id: 6,
      title: 'University Library',
      category: 'Institutional',
      description: 'Learning spaces that inspire knowledge',
      fullDescription: 'A modern university library designed to support various learning styles with quiet study areas, collaborative spaces, and technology-integrated research facilities.',
      icons: [GraduationCap, Lightbulb, Eye, Target, CheckCircle, Star],
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      location: 'University of Hyderabad',
      completionDate: 'August 2023',
      size: '20,000 sq ft',
      duration: '12 months',
      features: ['Study Areas', 'Digital Integration', 'Acoustic Design', 'Flexible Spaces']
    }
  ];

  const categories = [
    { name: 'All', count: projects.length },
    { name: 'Residential', count: projects.filter(p => p.category === 'Residential').length },
    { name: 'Commercial', count: projects.filter(p => p.category === 'Commercial').length },
    { name: 'Hospitality', count: projects.filter(p => p.category === 'Hospitality').length },
    { name: 'Institutional', count: projects.filter(p => p.category === 'Institutional').length }
  ];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Editorial Pattern */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={8}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  {/* Badge Component */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Our Portfolio
                    </Typography>
                  </Box>
                  
                  {/* Headline */}
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Transforming{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Visions
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Into Reality
                    </Box>
                  </Typography>
                  
                  {/* Subtitle */}
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Explore our diverse portfolio of residential, commercial, hospitality, and institutional projects. 
                    Each design tells a unique story of innovation, creativity, and exceptional craftsmanship.
                  </Typography>
                  
                  {/* Stats Section */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
                      }}>
                        <CheckCircle size={24} color="white" />
                      </Box>
                      <Typography 
                        color="#1A1A1A"
                        sx={{ 
                          fontWeight: 500,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        50+ Completed Projects
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
                      }}>
                        <Award size={24} color="white" />
                      </Box>
                      <Typography 
                        color="#1A1A1A"
                        sx={{ 
                          fontWeight: 500,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Award-Winning Designs
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 3,
                      width: '100%',
                      maxWidth: 240,
                      zIndex: 2
                    }}
                  >
                    {[Home, Building2, Hotel, Palette, Lightbulb, Heart, Target, Star, Award].map((IconComponent, index) => (
                      <Zoom key={index} in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                            animation: `iconFloat 3s ease-in-out ${index * 0.1}s infinite alternate`,
                            '@keyframes iconFloat': {
                              '0%': { transform: 'translateY(0)' },
                              '100%': { transform: 'translateY(-8px)' }
                            }
                          }}
                        >
                          <IconComponent size={24} />
                        </Box>
                      </Zoom>
                    ))}
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Filter Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          position: 'relative',
          background: '#FFFFFF',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Fade in={showElements}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <Filter size={24} style={{ marginRight: 12, color: '#FF5722' }} />
                  <Typography
                    variant="h5"
                    color="#1A1A1A"
                    sx={{ 
                      fontWeight: 600,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Filter by Category
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                  {categories.map((category) => (
                    <FilterChip
                      key={category.name}
                      label={category.name}
                      count={category.count}
                      active={selectedCategory === category.name}
                      onClick={() => setSelectedCategory(category.name)}
                    />
                  ))}
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography 
                  variant="overline" 
                  color="#999" 
                  sx={{ 
                    mb: 2, 
                    display: 'block',
                    fontWeight: 500,
                    letterSpacing: 3,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Featured Work
                </Typography>
                <Typography
                  variant="h2"
                  component="h2"
                  color="#1A1A1A"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {selectedCategory === 'All' ? 'All Projects' : `${selectedCategory} Projects`}
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  color="#666"
                  sx={{
                    maxWidth: 700,
                    mx: 'auto',
                    fontWeight: 400,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} found
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {filteredProjects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard 
                  project={project} 
                  delay={300 * (index + 1)} 
                  onClick={handleProjectClick}
                />
              </Grid>
            ))}
          </Grid>

          {filteredProjects.length === 0 && (
            <Fade in={true}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography 
                  variant="h5" 
                  color="#999" 
                  gutterBottom
                  sx={{ 
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  No projects found in this category
                </Typography>
                <Typography 
                  variant="body1" 
                  color="#666"
                  sx={{ 
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Try selecting a different category or view all projects
                </Typography>
              </Box>
            </Fade>
          )}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                component="h2"
                color="white"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Ready for Your Project?
              </Typography>
              <Typography
                variant="h5"
                component="p"
                color="white"
                sx={{
                  mb: 6,
                  fontWeight: 400,
                  opacity: 0.9,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Let's discuss your vision and create something extraordinary together.
              </Typography>
              <Button
                href="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowRight />}
                sx={{
                  borderRadius: 0,
                  px: 6,
                  py: 2,
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0,0,0,0.3)',
                    background: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                Start Your Project
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Project Detail Modal */}
      <ProjectDetailModal
        project={selectedProject}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default PortfolioPage;